import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * Board of Directors grid — 3 responsive cards beneath the Founder feature.
 *
 * Each card is independently expandable so a reader can dig into one bio
 * without disturbing the others. Visual language deliberately matches
 * FounderSection (rounded photo well, brand shadow, soft border) but at
 * a smaller scale so Sara's founder card remains the dominant figure.
 */

const BOARD = [
  {
    slug: "natalie-smith",
    name: "Natalie Smith",
    title: "Board Member, Community Engagement & Education",
    photo:
      "https://customer-assets.emergentagent.com/job_safe-circle-13/artifacts/coio728i_natalie%20square%20photo.jpg",
    summary:
      "Instructional Designer with a multidisciplinary background in psychology, learning design, and lived advocacy.",
    paragraphs: [
      "Natalie Smith serves on the Sister Signal Board of Directors and brings both lived experience and a deep commitment to advocacy and community impact to the organization.",
      "After navigating harmful relationships in her own life, Sister Signal's mission is one that resonates personally, and she is passionate about helping create resources that empower others to recognize unhealthy relationship patterns, rebuild self-trust, and find support.",
      "Natalie lives in Iowa with her husband and two cats. In addition to her work with Sister Signal, she is active in civic engagement and serves as a part-time political organizer, championing causes that strengthen and support communities.",
      "Professionally, Natalie works full-time as an Instructional Designer and brings a multidisciplinary background in psychology, learning design, and graphic design. Her expertise helps inform thoughtful, accessible educational experiences that make complex topics easier to understand and navigate.",
      "Outside of work and volunteer service, Natalie enjoys road trips with her husband, long walks, creating art, and discovering hidden treasures while thrifting.",
    ],
  },
  {
    slug: "alyx-coble-frakes",
    name: "Alyx Coble-Frakes",
    title: "Board Member, Platform Strategy & Survivor Advocacy",
    photo:
      "https://customer-assets.emergentagent.com/job_safe-circle-13/artifacts/tm8ljguj_alyx.JPG",
    summary:
      "Founder of The Agenda Period; advises on platform strategy, user experience, and survivor-centered product development.",
    paragraphs: [
      "Alyx Coble-Frakes brings both lived experience and an entrepreneurial background in women's health technology to her role on Sister Signal's Board of Directors. As the nontechnical founder of The Agenda Period — a cycle health app, planner line, and coaching practice she built and led for seven years — Alyx worked closely on product development, user experience, and platform strategy, gaining firsthand insight into how complex personal experiences can be translated into tools that people can meaningfully use in their daily lives.",
      "That same pattern-recognition informs her understanding of coercive control, emotional abuse, and unhealthy relationship dynamics. Drawing from both personal and professional experience, Alyx helps guide Sister Signal's survivor-centered approach to education, resources, and outreach.",
      "As a board member, she advises on platform strategy, user experience, and content development, helping ensure that Sister Signal remains accessible, practical, and deeply responsive to the needs of survivors.",
    ],
  },
  {
    slug: "amana-katora",
    name: "Amana Katora",
    title: "Community Advisor & Strategic Partnerships",
    photo:
      "https://customer-assets.emergentagent.com/job_safe-circle-13/artifacts/cmtrjpj0_amana.jpg",
    summary:
      "Cybersecurity compliance assessor (CMMC, ISO 27001) bringing governance, strategic partnerships, and stakeholder communications expertise.",
    paragraphs: [
      "Amana Katora brings a sharp, systems-oriented mind and a deep commitment to community impact to Sister Signal. Professionally, she is a cybersecurity compliance assessor specializing in CMMC, ISO 27001, and related governance frameworks, with extensive experience guiding organizations through complex federal assessments for U.S. Department of Defense and Intelligence Community clients.",
      "Known for translating technical complexity into clear, actionable outcomes, Amana also brings a strong background in proposal management, strategic partnerships, and stakeholder communications.",
      "She channels that same dedication to structure, clarity, and advocacy into supporting Sister Signal's mission to raise awareness and drive meaningful change around domestic violence in our community.",
    ],
  },
];

function BoardCard({ member }) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = member.paragraphs.length > 0;

  return (
    <article
      data-testid={`board-card-${member.slug}`}
      className="rounded-3xl bg-card border border-border p-6 flex flex-col"
    >
      <div className="aspect-square rounded-2xl overflow-hidden bg-brand-soft/50 shadow-[0_18px_40px_-20px_rgba(214,90,140,0.25)]">
        <img
          src={member.photo}
          alt={`${member.name}, ${member.title}`}
          loading="lazy"
          className="w-full h-full object-cover"
          data-testid={`board-photo-${member.slug}`}
        />
      </div>

      <div className="mt-5 flex-1 flex flex-col">
        <h3 className="font-serif text-2xl leading-tight">{member.name}</h3>
        <p className="mt-1 text-xs uppercase tracking-widest text-foreground/55">
          {member.title}
        </p>
        <p className="mt-3 text-foreground/80 leading-relaxed text-sm">
          {member.summary}
        </p>

        {hasMore && (
          <div
            className={`grid transition-all duration-500 ease-in-out ${
              expanded ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0"
            }`}
            data-testid={`board-bio-extended-${member.slug}`}
            aria-hidden={!expanded}
          >
            <div className="overflow-hidden space-y-3 text-foreground/80 leading-relaxed text-sm">
              {member.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        )}

        {hasMore && (
          <button
            data-testid={`board-toggle-${member.slug}`}
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 inline-flex items-center gap-1 text-sm text-brand hover:underline underline-offset-4 self-start"
            aria-expanded={expanded}
          >
            {expanded ? (
              <>Read less <ChevronUp className="w-4 h-4" /></>
            ) : (
              <>Read more <ChevronDown className="w-4 h-4" /></>
            )}
          </button>
        )}
      </div>
    </article>
  );
}

export default function BoardSection() {
  return (
    <section
      className="max-w-5xl mx-auto px-6 sm:px-10 pt-4 pb-16"
      data-testid="board-section"
      aria-labelledby="board-heading"
    >
      <div className="max-w-3xl">
        <p className="font-script text-xl text-brand">board of directors</p>
        <h3 id="board-heading" className="mt-1 font-serif text-3xl sm:text-4xl leading-tight">
          A small, fierce circle.
        </h3>
        <p className="mt-3 text-foreground/75 leading-relaxed">
          Sister Signal is guided by a board of survivors, builders, educators, and advocates — each
          bringing lived experience and professional craft to the work.
        </p>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {BOARD.map((m) => (
          <BoardCard key={m.slug} member={m} />
        ))}
      </div>
    </section>
  );
}
